import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useSelector } from 'react-redux';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  useCreateAlertConfig,
  useUpdateAlertConfig,
  useAlertConfigById,
  CreateAlertConfigPayload,
} from './hooks/useAlertConfigs';
import { selectGroupGuid } from '../../slices/commonSelectors';
import { PratusStepper } from '../../components/DesignSystems/PratusStepper/PratusStepper';
import { StylishNewButton } from '../../components/DesignSystems/New/StylishNewButton';
import PageTitle from '../headers/PageTitle';
import BasicInformationStep from './steps/BasicInformationStep';
import TriggersStep from './steps/TriggersStep';
import CIRsStep from './steps/CIRsStep';
import TestFinalizeStep from './steps/TestFinalizeStep';
import { Spinner, Alert } from 'react-bootstrap';

type AlertConfigWizardPageProps = {
  mode: 'create' | 'edit';
};

const AlertConfigWizardPage: React.FC<AlertConfigWizardPageProps> = ({
  mode,
}) => {
  const navigate = useNavigate();
  const { alert_config_id } = useParams<{ alert_config_id: string }>();
  const groupGuid = useSelector(selectGroupGuid);

  const createConfig = useCreateAlertConfig();
  const updateConfig = useUpdateAlertConfig();

  // Fetch existing config if in edit mode
  const {
    data: existingConfig,
    isLoading: isLoadingConfig,
    error: configError,
  } = useAlertConfigById(mode === 'edit' ? alert_config_id || '' : '');

  const [activeStep, setActiveStep] = useState(1);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const isNotTested = false;

  const form = useForm<CreateAlertConfigPayload>({
    mode: 'onChange',
    defaultValues: {
      name: '',
      eventType: '',
      refreshCadence: '*/5 * * * *',
      aorId: '',
      active: false,
      triggers: [],
      cirs: [],
    } as CreateAlertConfigPayload,
  });

  const { handleSubmit } = form;
  const { isValid, isDirty } = form.formState;

  // Get current form values for validation using getValues to avoid infinite loops
  const getCurrentValues = () => {
    const values = form.getValues();
    return {
      triggers: values.triggers || [],
      cirs: values.cirs || [],
    };
  };

  // Validation logic for each step
  const isStepValid = () => {
    const { triggers, cirs } = getCurrentValues();
    switch (activeStep) {
      case 1: // Basic Information
        return isValid;
      case 2: // Triggers
        return isValid && triggers.length > 0;
      case 3: // CIRs
        return isValid && cirs.length > 0;
      case 4: // Test and Finalize
        return isValid;
      default:
        return isValid;
    }
  };

  // Additional validation for edit mode - ensure we have changes
  const canProceed = () => {
    if (mode === 'edit') {
      return isStepValid();
    } else {
      return isStepValid() && isDirty;
    }
  };

  // Define steps for the stepper
  const steps = [
    { id: 1, label: 'Basic Information' },
    { id: 2, label: 'Set Triggers' },
    { id: 3, label: 'Set CIRs' },
    { id: 4, label: 'Test and Finalize' },
  ];

  // Load existing config data into form when editing
  React.useEffect(() => {
    if (mode === 'edit' && existingConfig) {
      form.reset({
        ...existingConfig,
        // Ensure all required fields are present
        name: existingConfig.name || '',
        eventType: existingConfig.eventType || '',
        refreshCadence: existingConfig.refreshCadence || '*/5 * * * *',
        aorId: existingConfig.aorId || '',
        active: existingConfig.active || false,
        triggers: existingConfig.triggers || [],
        cirs: existingConfig.cirs || [],
      });
    }
  }, [existingConfig, mode, form]);

  // Modified handleSaveConfig to be async and directly return mutateAsync result
  const handleSaveConfig = async (data: CreateAlertConfigPayload) => {
    const payload = {
      ...data,
      eventType: data.eventType,
      groupGuid: groupGuid,
      active: data.active,
      archived: false,
    };

    try {
      // Check if this is an update operation (has an ID) or a create operation
      if (mode === 'edit' && alert_config_id) {
        // Ensure the ID is set for update
        // Await and return the result of update mutation
        const updatedData = await updateConfig.mutateAsync({
          ...payload,
          id: alert_config_id,
        });
        return updatedData;
      } else {
        // No ID, so this is a create operation
        // Await and return the result of create mutation
        const createdData = await createConfig.mutateAsync(payload);
        return createdData;
      }
    } catch (error) {
      console.error('Error saving configuration:', error);
      toast.error(
        'Failed to save configuration. Please check the details and try again.'
      );
      throw error;
    }
  };

  const handleNext = async (savedData: CreateAlertConfigPayload) => {
    if (activeStep === steps.length) {
      // This is the final step, create/update the configuration
      try {
        const result = await handleSaveConfig(savedData);
        // Navigate to the config summary page on success
        if (result?.id) {
          navigate(`/aialerts/${result.id}`);
        } else if (alert_config_id) {
          navigate(`/aialerts/${alert_config_id}`);
        } else {
          navigate('/aialerts');
        }
      } catch (error) {
        console.error('Error creating/updating configuration:', error);
        toast.error(
          `Failed to ${mode === 'edit' ? 'update' : 'create'} configuration. Please check the details and try again.`
        );
      }
    } else {
      // For other steps, just proceed to next step
      setSelectedTemplate(savedData || selectedTemplate);
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => {
    if (activeStep > 1) {
      setActiveStep(activeStep - 1);
    } else {
      // If we're on the first step, go back to the alerts list
      navigate('/aialerts');
    }
  };

  const renderActiveStep = () => {
    switch (activeStep) {
      case 1:
        return <BasicInformationStep />;
      case 2:
        return <TriggersStep />;
      case 3:
        return <CIRsStep />;
      case 4:
        return <TestFinalizeStep />;
      default:
        return null;
    }
  };

  if (mode === 'edit' && isLoadingConfig) {
    return (
      <div className="p-4 pt-0">
        <PageTitle
          title="Edit Alert Configuration"
          parents={['Alert Configurations']}
        />
        <div className="d-flex justify-content-center align-items-center p-5">
          <Spinner animation="border" />
        </div>
      </div>
    );
  }

  if (mode === 'edit' && configError) {
    return (
      <div className="p-4 pt-0">
        <PageTitle
          title="Edit Alert Configuration"
          parents={['Alert Configurations']}
        />
        <Alert variant="danger" className="mt-3">
          <strong>Error loading configuration:</strong>{' '}
          {configError.message || 'Failed to load configuration data'}
        </Alert>
        <div className="mt-3">
          <StylishNewButton
            className="button--secondary"
            onClick={() => navigate('/aialerts')}
          >
            Back to Alert Configurations
          </StylishNewButton>
        </div>
      </div>
    );
  }

  const pageTitle =
    mode === 'edit' ? 'Edit Alert Configuration' : 'Create Alert Configuration';
  const parents = ['Alert Configurations'];

  return (
    <div className="p-4 pt-0">
      <PageTitle title={pageTitle} parents={parents} />

      <div className="row">
        {/* Stepper sidebar */}
        <div className="col-md-3">
          <div className="mb-4">
            <PratusStepper steps={steps} activeStep={activeStep} vertical />
          </div>
        </div>

        {/* Main Content */}
        <div className="col-md-9">
          <FormProvider {...form}>
            <form onSubmit={handleSubmit(handleNext)}>
              <div className="mb-4">{renderActiveStep()}</div>

              {/* Action Buttons */}
              <div className="d-flex gap-2">
                <StylishNewButton
                  type="button"
                  className="button--secondary"
                  onClick={handleBack}
                >
                  {activeStep === 1 ? 'Cancel' : 'Back'}
                </StylishNewButton>

                {activeStep < steps.length ? (
                  <StylishNewButton
                    type="submit"
                    className="button--primary"
                    disabled={!canProceed()}
                  >
                    Next
                  </StylishNewButton>
                ) : (
                  <StylishNewButton
                    type="submit"
                    className="button--primary"
                    disabled={!canProceed() || isNotTested}
                  >
                    {mode === 'edit'
                      ? 'Update Configuration'
                      : 'Create Configuration'}
                  </StylishNewButton>
                )}
              </div>
            </form>
          </FormProvider>
        </div>
      </div>
    </div>
  );
};

export default AlertConfigWizardPage;
